import ReportImagePicker from '@components/Customization/ReportImagePicker';
import { storedImageStatus } from '@components/Customization/reportImageStatus';
import { type ReportImageRef } from '@store/api/reportDesignEndpoints';
import { useDesignPicture } from './useDesignPicture';

/** What the editor knows about its design's images, and how one is added. */
export interface DesignImages {
  designId: string;
  /** The files picked during this visit, as addresses a picture can show, by the id each was stored under. */
  local: Readonly<Record<string, string>>;
  /** The ids the server has answered for with "no such image". */
  missing: ReadonlySet<string>;
  /**
   * True while a file of this editor is on its way, whichever field it was picked for. No
   * field takes a file or lets its image go meanwhile: the file is put into the draft when
   * it has been stored, over whatever was done to its field since. The control that took the
   * file cannot say so itself, since the inspector is mounted anew whenever the selection
   * changes and knows nothing then of what it was handed before.
   */
  uploading: boolean;
  /**
   * Stores a file and hands its id to `place`, in turn with the editor's other writes, so a
   * save asked for while the file is on its way carries the image. Answers whether it was stored.
   */
  upload: (file: File, place: (imageId: string) => void) => Promise<boolean>;
  reportMissing: (imageId: string) => void;
}

interface Props {
  label: string;
  /** A sentence under the control. */
  help?: string;
  value: ReportImageRef | null | undefined;
  /** `undefined` takes the image off. */
  onChange: (value: ReportImageRef | undefined) => void;
  images: DesignImages;
  disabled?: boolean;
}

/**
 * One uploaded image of a design: the theme's logo, or what an image block draws.
 *
 * A file is sent the moment it is picked, and the draft is given the id it was stored under;
 * the design refers to the image only once that draft is saved. Until then the picture on
 * screen is the picked file itself, and on a later visit it is read from the server, which
 * hands an image only to a request that carries the session.
 *
 * An image the server no longer has is said so here, with the way out: a design that names
 * one cannot be saved.
 */
const ImageField: React.FC<Props> = ({ label, help, value, onChange, images, disabled }) => {
  const imageId = value?.imageId;
  const { picture, gone, reading, error } = useDesignPicture(images, imageId);

  return (
    <ReportImagePicker
      label={label}
      help={help}
      status={storedImageStatus({ imageId, picture, gone, reading, error })}
      picture={picture}
      onPick={(file) => images.upload(file, (storedId) => onChange({ imageId: storedId }))}
      onRemove={() => onChange(undefined)}
      disabled={disabled || images.uploading}
    />
  );
};

export default ImageField;
